import React from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FlightIcon from '@mui/icons-material/Flight';
import ComputerIcon from '@mui/icons-material/Computer';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { AMEX_CALL_CENTER_PHONE, FISERV_PORTAL_URL, VISA_CALL_CENTER_PHONE } from '../constants/authorizationChannels';
import { CarrierChannelSummaryType } from '../helpers/carrierReference';
import { copyToClipboard } from 'features/transaction-details/helpers';

interface CarrierChannelSummaryProps {
	carrierName: string;
	summaryType: CarrierChannelSummaryType;
	onCopied?: (message: string) => void;
}

export const CarrierChannelSummary = ({
	carrierName,
	summaryType,
	onCopied,
}: CarrierChannelSummaryProps): JSX.Element => {
	const handleCopyPhone = async (phone: string): Promise<void> => {
		const success = await copyToClipboard(phone);
		if (success) onCopied?.('Teléfono copiado');
	};

	if (summaryType === 'fiserv_always') {
		return (
			<Alert severity="info" icon={<ComputerIcon />} sx={{ border: 1, borderColor: 'info.light' }}>
				<Typography variant="subtitle2" fontWeight="bold" gutterBottom>
					Canal de autorización — Fiserv
				</Typography>
				<Typography variant="body2" gutterBottom>
					{carrierName}: autorizá siempre por el panel Fiserv, con cualquier sello de tarjeta habilitado.
				</Typography>
				<Typography variant="body2" color="text.secondary" gutterBottom>
					American Express se autoriza por call center AX {AMEX_CALL_CENTER_PHONE}, no por Fiserv.
				</Typography>
				<Button
					variant="contained"
					size="small"
					startIcon={<OpenInNewIcon />}
					href={FISERV_PORTAL_URL}
					target="_blank"
					rel="noopener noreferrer"
				>
					Abrir Fiserv
				</Button>
			</Alert>
		);
	}

	if (summaryType === 'fiserv_except_visa') {
		return (
			<Alert severity="warning" icon={<PhoneIcon />} sx={{ border: 1, borderColor: 'warning.light' }}>
				<Typography variant="subtitle2" fontWeight="bold" gutterBottom>
					Canal de autorización — según sello
				</Typography>
				<Typography variant="body2" component="div" gutterBottom>
					<strong>{carrierName}</strong>
					<ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
						<li>
							<strong>Visa</strong> → call center <strong>{VISA_CALL_CENTER_PHONE}</strong> (no Fiserv)
						</li>
						<li>
							<strong>American Express</strong> → call center AX <strong>{AMEX_CALL_CENTER_PHONE}</strong> (no Fiserv)
						</li>
						<li>
							<strong>Mastercard, Oca</strong> y otros → Fiserv
						</li>
					</ul>
				</Typography>
				<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
					<Button variant="outlined" size="small" onClick={() => handleCopyPhone(VISA_CALL_CENTER_PHONE)}>
						Copiar Visa {VISA_CALL_CENTER_PHONE}
					</Button>
					<Button variant="outlined" size="small" onClick={() => handleCopyPhone(AMEX_CALL_CENTER_PHONE)}>
						Copiar Amex {AMEX_CALL_CENTER_PHONE}
					</Button>
					<Button
						variant="contained"
						size="small"
						startIcon={<OpenInNewIcon />}
						href={FISERV_PORTAL_URL}
						target="_blank"
						rel="noopener noreferrer"
					>
						Abrir Fiserv
					</Button>
				</Box>
			</Alert>
		);
	}

	if (summaryType === 'gds') {
		return (
			<Alert severity="info" icon={<FlightIcon />} sx={{ border: 1, borderColor: 'divider' }}>
				<Typography variant="subtitle2" fontWeight="bold" gutterBottom>
					Canal de autorización — GDS
				</Typography>
				<Typography variant="body2">
					{carrierName}: venta internacional, un solo pago. La autorización se gestiona por GDS, no por Fiserv ni call
					center {VISA_CALL_CENTER_PHONE}.
				</Typography>
			</Alert>
		);
	}

	if (summaryType === 'no_cards') {
		return (
			<Alert severity="error">
				<Typography variant="subtitle2" fontWeight="bold" gutterBottom>
					{carrierName} no acepta tarjetas
				</Typography>
				<Typography variant="body2">Verificá el procedimiento con supervisión antes de autorizar.</Typography>
			</Alert>
		);
	}

	return (
		<Alert severity="info" icon={<HelpOutlineIcon />}>
			<Typography variant="subtitle2" fontWeight="bold" gutterBottom>
				Canal de autorización
			</Typography>
			<Typography variant="body2" gutterBottom>
				{carrierName}: no hay regla fija de canal en el sistema. En general usá Fiserv según la tabla de comercios; si
				tenés dudas, consultá con supervisión.
			</Typography>
			<Typography variant="body2" color="text.secondary" gutterBottom>
				American Express → call center AX {AMEX_CALL_CENTER_PHONE}.
			</Typography>
			<Button
				variant="outlined"
				size="small"
				startIcon={<OpenInNewIcon />}
				href={FISERV_PORTAL_URL}
				target="_blank"
				rel="noopener noreferrer"
			>
				Abrir Fiserv
			</Button>
		</Alert>
	);
};
